import type { GlobalSearchResult } from "@shared/api/globalSearch";
import type { GlobalNavigationTarget } from "@shared/navigation/globalNavigation";
import type { SidebarItem } from "@shared/components/Sidebar/sidebar.config";

type NavigationKind = "apartado" | "subapartado" | "seccion" | "subseccion";

type NavigationEntry = {
  id: string;
  type: NavigationKind;
  title: string;
  parent: string;
  description: string;
  url: string;
  accessUrl: string;
  keywords?: string[];
  navigation?: GlobalNavigationTarget;
};

const INTERNAL_ENTRIES: NavigationEntry[] = [
  {
    id: "profile",
    type: "seccion",
    title: "Mi perfil",
    parent: "Perfil",
    description: "Datos personales y preferencias de la cuenta.",
    url: "/profile",
    accessUrl: "/profile",
    keywords: ["perfil", "mi cuenta", "cuenta", "usuario"],
  },

  // Panel de administración
  ...[
    ["usuarios", "Usuarios", "Listado, empleados y permisos"],
    ["fichajes", "Fichajes", "Control horario, retrasos, faltas y justificaciones"],
    ["horarios", "Horarios", "Gestión de turnos por empleado"],
    ["agenda", "Agenda", "Eventos y reuniones asignados a la plantilla"],
    ["nominas", "Nóminas", "Gestión de nóminas y documentos"],
    ["contratos", "Contratos", "Altas y consulta de contratos"],
    ["vacaciones", "Vacaciones", "Solicitudes, calendario y saldo"],
    ["estadisticas", "Estadísticas", "Asistencia, incidencias y vacaciones"],
  ].map(([section, title, description]) => ({
    id: `admin-${section}`,
    type: "seccion" as const,
    title,
    parent: "Panel de administración",
    description,
    url: "/admin-panel",
    accessUrl: "/admin-panel",
    navigation: { scope: "admin-panel", section },
    keywords: ["administración", "admin", title],
  })),
  {
    id: "admin-descansos-plantilla",
    type: "subseccion",
    title: "Días de descanso de la plantilla",
    parent: "Panel de administración · Horarios",
    description: "Calendario anual de descansos comunes para toda la plantilla.",
    url: "/admin-panel",
    accessUrl: "/admin-panel",
    navigation: { scope: "admin-panel", section: "horarios" },
    keywords: ["descansos", "días libres", "dias libres", "horarios", "plantilla"],
  },
  {
    id: "admin-justificaciones",
    type: "subseccion",
    title: "Justificaciones de fichaje",
    parent: "Panel de administración · Fichajes",
    description: "Abre el control horario donde se revisan las justificaciones.",
    url: "/admin-panel",
    accessUrl: "/admin-panel",
    navigation: { scope: "admin-panel", section: "fichajes" },
    keywords: ["justificaciones", "fichajes", "control horario", "rrhh"],
  },
  {
    id: "admin-solicitudes-vacaciones",
    type: "subseccion",
    title: "Solicitudes de vacaciones",
    parent: "Panel de administración · Vacaciones",
    description: "Abre la gestión de solicitudes, calendario y saldo de vacaciones.",
    url: "/admin-panel",
    accessUrl: "/admin-panel",
    navigation: { scope: "admin-panel", section: "vacaciones" },
    keywords: ["solicitudes", "vacaciones", "aprobar", "rechazar"],
  },
  {
    id: "admin-chat",
    type: "subseccion",
    title: "Chat de administración",
    parent: "Panel de administración",
    description: "Comunicación interna del Panel de administración.",
    url: "/admin-panel",
    accessUrl: "/admin-panel",
    keywords: ["chat", "conversaciones", "mensajes", "administración"],
  },
  {
    id: "admin-chat-grupos",
    type: "subseccion",
    title: "Grupos de chat",
    parent: "Panel de administración · Chat",
    description: "Creación y gestión de conversaciones grupales internas.",
    url: "/admin-panel",
    accessUrl: "/admin-panel",
    keywords: ["grupos", "chat", "miembros", "conversaciones"],
  },

  {
    id: "admin-nominas-auto",
    type: "subseccion",
    title: "Generación automática de nóminas",
    parent: "Panel de administración · Nóminas",
    description: "Abre la generación automática de nóminas.",
    url: "/admin-panel",
    accessUrl: "/admin-panel",
    navigation: { scope: "admin-panel", section: "nominas", subsection: "auto" },
    keywords: ["nóminas", "nominas", "ia", "automática", "automatica"],
  },
  {
    id: "admin-nominas-manual",
    type: "subseccion",
    title: "Subida manual de nóminas",
    parent: "Panel de administración · Nóminas",
    description: "Abre la carga manual de nóminas.",
    url: "/admin-panel",
    accessUrl: "/admin-panel",
    navigation: { scope: "admin-panel", section: "nominas", subsection: "manual" },
    keywords: ["nóminas", "nominas", "manual", "subida"],
  },

  // Recursos Humanos
  ...[
    ["fichaje", "Fichaje", "Entrada, salida, contador y justificaciones"],
    ["vacaciones", "Vacaciones", "Solicitudes y saldo de vacaciones"],
    ["agenda", "Agenda", "Calendario y eventos personales"],
    ["nominas", "Nóminas", "Descargas mensuales"],
    ["documentacion", "Documentación", "Contratos y anexos"],
  ].map(([section, title, description]) => ({
    id: `rrhh-${section}`,
    type: "seccion" as const,
    title,
    parent: "Recursos Humanos",
    description,
    url: "/rrhh",
    accessUrl: "/rrhh",
    navigation: { scope: "rrhh", section },
    keywords: ["rrhh", "recursos humanos", title],
  })),

  {
    id: "rrhh-solicitudes-vacaciones",
    type: "subseccion",
    title: "Solicitudes de vacaciones",
    parent: "Recursos Humanos · Vacaciones",
    description: "Abre las solicitudes y el saldo personal de vacaciones.",
    url: "/rrhh",
    accessUrl: "/rrhh",
    navigation: { scope: "rrhh", section: "vacaciones" },
    keywords: ["solicitudes", "vacaciones", "saldo", "rrhh"],
  },
  {
    id: "rrhh-chat",
    type: "subseccion",
    title: "Chat de Recursos Humanos",
    parent: "Recursos Humanos",
    description: "Comunicación interna con Recursos Humanos.",
    url: "/rrhh",
    accessUrl: "/rrhh",
    keywords: ["chat", "conversaciones", "mensajes", "rrhh"],
  },
  {
    id: "rrhh-chat-grupos",
    type: "subseccion",
    title: "Grupos de chat",
    parent: "Recursos Humanos · Chat",
    description: "Conversaciones grupales en las que participa el usuario.",
    url: "/rrhh",
    accessUrl: "/rrhh",
    keywords: ["grupos", "chat", "miembros", "conversaciones"],
  },

  // Tareas
  ...[
    ["kanban", "Kanban", "Vista Kanban de tareas"],
    ["tabla", "Tabla", "Vista de tabla de tareas"],
    ["dashboard", "Dashboard", "Métricas y actividad de tareas"],
  ].map(([section, title, description]) => ({
    id: `tasks-${section}`,
    type: "seccion" as const,
    title,
    parent: "Tareas",
    description,
    url: "/tasks",
    accessUrl: "/tasks",
    navigation: { scope: "tasks", section },
    keywords: ["tareas", "vista", title],
  })),

  // IT
  ...[
    ["kanban", "Kanban", "Workflow y estados de incidencias IT"],
    ["tabla", "Tabla", "Vista operativa de incidencias IT"],
    ["dashboard", "Dashboard", "Carga, tiempos y reaperturas de IT"],
  ].map(([section, title, description]) => ({
    id: `it-${section}`,
    type: "seccion" as const,
    title,
    parent: "IT",
    description,
    url: "/it",
    accessUrl: "/it",
    navigation: { scope: "it", section },
    keywords: ["it", "incidencias", "soporte", title],
  })),
  {
    id: "it-incidencias",
    type: "seccion",
    title: "Incidencias IT",
    parent: "IT",
    description: "Incidencias, solicitudes, validaciones y seguimiento técnico.",
    url: "/it",
    accessUrl: "/it",
    keywords: ["incidencias", "tickets", "solicitudes", "soporte"],
  },

  // Automatizaciones
  ...[
    ["briefing", "Briefing del día", "Resumen proactivo del día"],
    ["proposals", "Propuestas pendientes", "Acciones pendientes de aprobación"],
    ["rules", "Reglas", "Reglas y modos de automatización"],
    ["history", "Historial reciente", "Ejecuciones recientes"],
  ].map(([anchor, title, description]) => ({
    id: `automations-${anchor}`,
    type: "seccion" as const,
    title,
    parent: "Automatizaciones",
    description,
    url: "/automations",
    accessUrl: "/automations",
    navigation: { scope: "automations", anchor: `automations-${anchor}` },
    keywords: ["automatizaciones", "automatización", title],
  })),

  // Configuración · Recursos Humanos
  ...[
    ["departamentos", "Departamentos", "Departamentos por empresa"],
    ["vacaciones", "Vacaciones", "Política de vacaciones"],
    ["fichaje", "Fichaje / jornada", "Horario y tolerancia de fichaje"],
    ["festivos", "Festivos", "Calendario de días festivos"],
  ].map(([section, title, description]) => ({
    id: `settings-rrhh-${section}`,
    type: "seccion" as const,
    title,
    parent: "Configuración · Recursos Humanos",
    description,
    url: "/settings/rrhh",
    accessUrl: "/settings/rrhh",
    navigation: { scope: "settings-rrhh", section },
    keywords: ["configuración", "configuracion", "rrhh", "recursos humanos", title],
  })),

  // Certificaciones · Bibliotecas por estándar
  {
    id: "cert-iso9001-biblioteca",
    type: "seccion",
    title: "Biblioteca",
    parent: "Certificaciones · ISO 9001-14001",
    description: "Biblioteca de Calidad y Medioambiente.",
    url: "/certificaciones/iso-9001-14001/biblioteca",
    accessUrl: "/certificaciones/iso-9001-14001",
    keywords: ["biblioteca", "documentos", "documentación", "calidad", "medioambiente"],
  },
  ...[
    ["oficina", "Oficina", "/certificaciones/iso-9001-14001/biblioteca/oficina"],
    ["medioambiente", "Medioambiente", "/certificaciones/iso-9001-14001/biblioteca/medioambiente"],
    ["proveedores", "Proveedores", "/certificaciones/iso-9001-14001/biblioteca/proveedores"],
  ].map(([id, title, url]) => ({
    id: `cert-iso9001-biblioteca-${id}`,
    type: "subseccion" as const,
    title,
    parent: "Certificaciones · ISO 9001-14001 · Biblioteca",
    description: `Documentación de ${title.toLocaleLowerCase("es")}.`,
    url,
    accessUrl: "/certificaciones/iso-9001-14001",
    keywords: ["biblioteca", "documentos", "certificaciones", "iso 9001", "iso 14001", title],
  })),
  {
    id: "cert-iso27001-biblioteca",
    type: "seccion",
    title: "Biblioteca",
    parent: "Certificaciones · ISO 27001-27701",
    description: "Biblioteca de seguridad y privacidad.",
    url: "/certificaciones/iso-27001-27701/biblioteca",
    accessUrl: "/certificaciones/iso-27001-27701",
    keywords: ["biblioteca", "documentos", "documentación", "seguridad", "privacidad"],
  },
  ...[
    ["oficina", "Oficina", "/certificaciones/iso-27001-27701/biblioteca/oficina"],
    ["proveedores", "Proveedores", "/certificaciones/iso-27001-27701/biblioteca/proveedores"],
  ].map(([id, title, url]) => ({
    id: `cert-iso27001-biblioteca-${id}`,
    type: "subseccion" as const,
    title,
    parent: "Certificaciones · ISO 27001-27701 · Biblioteca",
    description: `Documentación de ${title.toLocaleLowerCase("es")}.`,
    url,
    accessUrl: "/certificaciones/iso-27001-27701",
    keywords: ["biblioteca", "documentos", "certificaciones", "iso 27001", "iso 27701", title],
  })),

  // ISO 9001-14001
  {
    id: "iso9001-main",
    type: "subapartado",
    title: "ISO 9001-14001",
    parent: "Certificaciones",
    description: "Calidad y medioambiente.",
    url: "/certificaciones/iso-9001-14001",
    accessUrl: "/certificaciones/iso-9001-14001",
    keywords: ["calidad", "medioambiente", "iso 9001", "iso 14001"],
  },
  ...[
    ["manual-sistema-integrado", "Manual del Sistema Integrado", "Manual integrado del estándar"],
    ["indicadores", "Indicadores", "Indicadores del sistema integrado"],
    ["incidencias", "Incidencias", "Incidencias, no conformidades y control documental"],
    ["suministradores", "Suministradores", "Suministradores homologados"],
    ["requisitos-legales", "Requisitos legales", "Matriz de requisitos legales"],
    ["objetivos", "Objetivos", "Objetivos de calidad y medioambiente"],
    ["dafo", "DAFO", "Debilidades, amenazas, fortalezas y oportunidades"],
    ["planificacion-de-cambios", "Planificación de cambios", "Planificación y control de cambios"],
    ["riesgos-oportunidades", "Riesgos y oportunidades", "Evaluación de riesgos y oportunidades"],
    ["mantenimiento-vehiculos", "Mantenimiento de vehículos", "Control de mantenimiento de vehículos"],
    ["mantenimiento-infraestructuras", "Mantenimiento de infraestructuras", "Control de mantenimiento de infraestructuras"],
    ["control-operacional", "Control operacional", "Control operacional del sistema"],
    ["formacion", "Formación", "Formación vinculada al sistema integrado"],
  ].map(([section, title, description]) => ({
    id: `iso9001-${section}`,
    type: "seccion" as const,
    title,
    parent: "Certificaciones · ISO 9001-14001",
    description,
    url: "/certificaciones/iso-9001-14001",
    accessUrl: "/certificaciones/iso-9001-14001",
    navigation: { scope: "cert-iso-9001-14001", section },
    keywords: ["iso 9001", "iso 14001", "calidad", "medioambiente", title],
  })),
  {
    id: "iso9001-incidencias-control",
    type: "subseccion",
    title: "Control documental de incidencias",
    parent: "Certificaciones · ISO 9001-14001 · Incidencias",
    description: "Abre la pestaña de control documental de Incidencias.",
    url: "/certificaciones/iso-9001-14001",
    accessUrl: "/certificaciones/iso-9001-14001",
    navigation: {
      scope: "cert-iso-9001-14001",
      section: "incidencias",
      subsection: "control",
    },
    keywords: ["control documental", "incidencias", "iso 9001", "iso 14001"],
  },
  {
    id: "iso9001-ficha-ncp",
    type: "subseccion",
    title: "Ficha NCp",
    parent: "Certificaciones · ISO 9001-14001 · Incidencias",
    description: "Abre Incidencias para seleccionar el Informe NCp correspondiente.",
    url: "/certificaciones/iso-9001-14001",
    accessUrl: "/certificaciones/iso-9001-14001",
    navigation: { scope: "cert-iso-9001-14001", section: "incidencias" },
    keywords: ["ncp", "informe ncp", "no conformidad", "incidencias"],
  },

  // ISO 27001-27701
  {
    id: "iso27001-main",
    type: "subapartado",
    title: "ISO 27001-27701",
    parent: "Certificaciones",
    description: "Privacidad y seguridad de la información.",
    url: "/certificaciones/iso-27001-27701",
    accessUrl: "/certificaciones/iso-27001-27701",
    keywords: ["privacidad", "seguridad", "iso 27001", "iso 27701", "sgsi"],
  },
  ...[
    ["manual-sistema-integrado", "Manual del Sistema Integrado", "Manual integrado de privacidad y seguridad"],
    ["indicadores", "Indicadores", "Indicadores de privacidad y seguridad"],
    ["soa", "SOA", "Declaración de aplicabilidad"],
    ["documentos-sistema", "Documentos", "Documentos del sistema"],
    ["inventario-dispositivos", "BG02 · Inventario de dispositivos", "Inventario de dispositivos físicos"],
    ["inventario-software", "BG03 · Inventario de software", "Inventario de software"],
    ["controles-informatica", "BG04 · Controles informática", "Controles y mantenimiento informático"],
  ].map(([section, title, description]) => ({
    id: `iso27001-${section}`,
    type: "seccion" as const,
    title,
    parent: "Certificaciones · ISO 27001-27701",
    description,
    url: "/certificaciones/iso-27001-27701",
    accessUrl: "/certificaciones/iso-27001-27701",
    navigation: { scope: "cert-iso-27001-27701", section },
    keywords: ["iso 27001", "iso 27701", "privacidad", "seguridad", "sgsi", title],
  })),
];

function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function flattenVisibleUrls(items: SidebarItem[]): Set<string> {
  const urls = new Set<string>(["/profile"]);

  for (const item of items) {
    if (!item.enabled) continue;
    urls.add(item.to);
    for (const child of item.children ?? []) {
      if (child.enabled) urls.add(child.to);
    }
  }

  return urls;
}

function sidebarResults(items: SidebarItem[]): GlobalSearchResult[] {
  const results: GlobalSearchResult[] = [];

  for (const item of items) {
    if (!item.enabled) continue;

    results.push({
      type: "apartado",
      section: item.label,
      title: item.label,
      subtitle: item.children?.length
        ? "Apartado principal de INPRONEXUS"
        : "Acceso directo al apartado",
      meta: "Navegación",
      url: item.to,
    });

    for (const child of item.children ?? []) {
      if (!child.enabled) continue;
      results.push({
        type: "subapartado",
        section: item.label,
        title: child.label,
        subtitle: `Subapartado de ${item.label}`,
        meta: `${item.label} · ${child.label}`,
        url: child.to,
      });
    }
  }

  results.push({
    type: "seccion",
    section: "Perfil",
    title: "Mi perfil",
    subtitle: "Datos personales y preferencias de la cuenta.",
    meta: "Perfil · Mi perfil",
    url: "/profile",
  });

  return results;
}

function scoreResult(result: GlobalSearchResult, query: string): number | null {
  const normalizedQuery = normalize(query);
  if (!normalizedQuery) return null;

  const terms = normalizedQuery.split(/\s+/).filter(Boolean);
  const title = normalize(result.title);
  const context = normalize(
    [result.section, result.title, result.subtitle ?? "", result.meta ?? ""].join(" "),
  );

  if (!terms.every((term) => context.includes(term))) return null;
  if (title === normalizedQuery) return 0;
  if (title.startsWith(normalizedQuery)) return 1;
  if (title.includes(normalizedQuery)) return 2;
  if (normalize(result.section).includes(normalizedQuery)) return 3;
  return 4;
}

function internalResults(items: SidebarItem[]): GlobalSearchResult[] {
  const visibleUrls = flattenVisibleUrls(items);

  return INTERNAL_ENTRIES
    .filter((entry) => visibleUrls.has(entry.accessUrl))
    .map((entry) => ({
      type: entry.type,
      section: entry.parent.split(" · ")[0] ?? entry.parent,
      title: entry.title,
      subtitle: entry.description,
      meta: entry.parent,
      url: entry.url,
      navigation: entry.navigation,
      searchKeywords: entry.keywords,
    }));
}

export function buildNavigationSearchResults(
  query: string,
  visibleItems: SidebarItem[],
): GlobalSearchResult[] {
  const base = [...sidebarResults(visibleItems), ...internalResults(visibleItems)];
  const normalizedQuery = normalize(query);

  const ranked = base
    .map((result) => {
      const keywords = result.searchKeywords?.join(" ") ?? "";
      const enriched = keywords
        ? { ...result, meta: [result.meta, keywords].filter(Boolean).join(" · ") }
        : result;
      return { result, score: scoreResult(enriched, normalizedQuery) };
    })
    .filter((item): item is { result: GlobalSearchResult; score: number } => item.score !== null)
    .sort((left, right) =>
      left.score !== right.score
        ? left.score - right.score
        : left.result.title.localeCompare(right.result.title, "es"),
    );

  const unique = new Map<string, GlobalSearchResult>();
  for (const { result } of ranked) {
    const navigationKey = result.navigation
      ? `${result.navigation.scope}:${result.navigation.section ?? ""}:${result.navigation.subsection ?? ""}:${result.navigation.anchor ?? ""}`
      : "";
    const key = `${result.type}:${result.url}:${result.title}:${navigationKey}`;
    if (!unique.has(key)) unique.set(key, result);
    if (unique.size >= 30) break;
  }

  return Array.from(unique.values());
}
